<template>
  <div
    data-tour="ai-chat"
    class="relative flex flex-1 flex-col overflow-hidden"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <DropZoneOverlay :visible="isDragging" />

    <div
      v-if="session.data"
      class="border-b border-gray-100 px-3 py-1.5 text-right text-xs text-gray-500"
    >
      {{ formatCredits(session.data.creditsUsed) }} credits used
    </div>

    <!-- Messages Container -->
    <div
      ref="messagesContainer"
      class="custom-table-scroll flex flex-1 flex-col gap-3 overflow-y-auto bg-gray-50 p-3"
    >
      <div
        v-for="message in displayMessages"
        :key="message.id"
        class="flex"
        :class="{
          'justify-end': message.role === 'user',
          'justify-start': message.role !== 'user',
        }"
      >
        <!-- AI message -->
        <div
          v-if="message.role === 'assistant'"
          class="flex max-w-3xl min-w-0 gap-2"
        >
          <div class="mt-1 flex-shrink-0">
            <FyrAiLogo :size="20" class="text-purple-600" />
          </div>
          <div class="min-w-0">
            <div class="rounded-lg border border-gray-200 bg-white px-3 py-2">
              <!-- eslint-disable vue/no-v-html -->
              <div
                class="ai-chat-content"
                :class="ANSWER_CLASS"
                v-html="
                  renderAnswer(parseAIContent(message.content).text, {
                    answer: answerNumbers.get(message.id),
                  })
                "
              ></div>
              <!-- eslint-enable vue/no-v-html -->
              <AIContextBox
                :entities="parseAIContent(message.content).entities"
              />
              <div
                v-if="message.toolCalls && message.toolCalls.length > 0"
                class="mt-2 space-y-1"
              >
                <AIToolCallSummary
                  v-for="(call, idx) in message.toolCalls"
                  :key="idx"
                  :name="call.name"
                  status="complete"
                />
              </div>
            </div>
            <!-- Quick-reply suggestion chips (only on last assistant message, when not streaming) -->
            <div
              v-if="
                isLastAssistantMessage(message.id) &&
                !streamState.isStreaming &&
                parseAIContent(message.content).suggestions.length > 0
              "
              class="mt-2 flex flex-wrap gap-2"
            >
              <button
                v-for="(suggestion, idx) in parseAIContent(message.content)
                  .suggestions"
                :key="idx"
                class="rounded-full border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-medium text-purple-700 transition hover:bg-purple-600 hover:text-white"
                :disabled="isTokenLimitExceeded"
                @click="handleSuggestionClick(suggestion)"
              >
                {{ suggestion }}
              </button>
            </div>
          </div>
        </div>

        <!-- User message -->
        <div
          v-else-if="message.role === 'user'"
          class="flex max-w-3xl min-w-0 flex-col items-end gap-1"
        >
          <div class="max-w-full rounded-lg bg-purple-600 px-3 py-2 text-white">
            <div class="text-sm wrap-anywhere whitespace-pre-wrap">
              {{ message.content }}
            </div>
            <!-- Attached images -->
            <div
              v-if="message.attachments?.length"
              class="mt-2 flex flex-wrap gap-2"
            >
              <a
                v-for="(att, idx) in message.attachments"
                :key="idx"
                :href="att.url"
                target="_blank"
                rel="noopener"
                class="block"
              >
                <img
                  :src="att.url"
                  :alt="att.filename || 'Attached image'"
                  class="max-h-40 max-w-60 rounded-md border border-purple-400"
                  loading="lazy"
                />
              </a>
            </div>
          </div>
          <div class="text-xs text-gray-400">
            {{ formatTime(message.createdAt) }}
          </div>
        </div>

        <!-- Tool messages are hidden — the AI summarises results in its response -->
      </div>

      <!-- Streaming indicator -->
      <div
        v-if="streamState.isStreaming"
        data-tour="ai-replying"
        class="flex gap-2"
      >
        <div class="mt-1 flex-shrink-0">
          <FyrAiLogo :size="20" class="text-purple-600" sparkle-loop />
        </div>
        <div
          class="min-w-0 rounded-lg border border-gray-200 bg-white px-4 py-3"
        >
          <div class="flex flex-col gap-2">
            <!-- Current message being streamed -->
            <!-- Streamed message content -->
            <!-- eslint-disable vue/no-v-html -->
            <div
              v-if="streamState.currentMessage"
              :class="ANSWER_CLASS"
              v-html="
                renderAnswer(stripMetaBlocks(streamState.currentMessage), {
                  streaming: true,
                })
              "
            ></div>
            <!-- eslint-enable vue/no-v-html -->

            <!-- Activity log (always visible during streaming when no message) -->
            <div v-if="!streamState.currentMessage" class="space-y-2">
              <!-- Completed tool calls -->
              <div v-if="completedToolCalls.length > 0" class="space-y-0.5">
                <AIToolCallSummary
                  v-for="(call, idx) in completedToolCalls"
                  :key="`done-${idx}`"
                  :name="call.name"
                  status="complete"
                />
              </div>

              <!-- Current activity: running tool or thinking -->
              <div class="flex items-center gap-2 text-sm text-gray-600">
                <Icon
                  name="spinner-third-duotone"
                  class="size-4 animate-spin text-purple-600"
                />
                <span>{{
                  runningToolCall ? runningToolLabel : thinkingMessage
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error message -->
      <div
        v-if="messages.error || streamState.error"
        class="rounded-md bg-red-50 p-3 text-sm text-red-800"
      >
        <p class="font-semibold">Error:</p>
        <p>{{ streamState.error || messages.error?.message }}</p>
      </div>
    </div>

    <div
      v-if="isTokenLimitExceeded"
      class="border-t border-gray-200 bg-orange-50 px-4 py-3 text-sm text-orange-800"
    >
      <p class="font-semibold">Monthly AI usage limit reached</p>
      <p>
        The limit will reset in {{ usage.data?.daysUntilRenewal }}
        {{ usage.data?.daysUntilRenewal === 1 ? "day" : "days" }}.
      </p>
    </div>

    <!-- Input Area -->
    <div class="border-t border-gray-200 p-3">
      <!-- Pending attachment previews -->
      <PendingAttachmentList
        class="mb-2"
        :attachments="pendingAttachments"
        :warning="attachmentWarning"
        @remove="removePendingAttachment"
      />

      <!-- The session payload carries the levels its model accepts; an empty
        list means the model takes no effort setting, so the control would be
        a choice with no effect. Offering exactly the served levels also keeps
        `Minimal` off models that reject it. -->
      <div v-if="supportedEfforts.length" class="mb-2">
        <ReasoningEffortSelect
          v-model="chatEffort"
          :options="supportedEfforts"
          :disabled="
            isSending ||
            isStopping ||
            isDraftSubmitting ||
            streamState.isStreaming ||
            isTokenLimitExceeded
          "
        />
      </div>

      <div class="flex gap-2">
        <!-- Hidden file input -->
        <input
          ref="fileInput"
          type="file"
          accept="image/png,image/jpeg"
          multiple
          class="hidden"
          @change="handleFileSelect"
        />
        <textarea
          ref="messageInput"
          v-model="newMessage"
          rows="2"
          class="max-h-100 min-h-16 flex-1 resize-none overflow-y-auto rounded-md border-gray-300 text-xs shadow-sm focus:border-purple-500 focus:ring-purple-500"
          placeholder="Continue the conversation..."
          :disabled="
            isSending ||
            isStopping ||
            isDraftSubmitting ||
            streamState.isStreaming ||
            isTokenLimitExceeded
          "
          @keydown.enter.exact.prevent="handleSend"
          @paste="handlePaste"
        />
        <div class="flex h-16 flex-col justify-end">
          <button
            type="button"
            class="mb-1 rounded-md px-2 text-gray-400 hover:text-purple-600 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="
              isSending ||
              isStopping ||
              isDraftSubmitting ||
              streamState.isStreaming ||
              isTokenLimitExceeded
            "
            title="Attach image"
            @click="fileInput?.click()"
          >
            <Icon name="image" class="size-6" />
          </button>
          <button
            v-if="streamState.isStreaming"
            class="rounded-md bg-red-600 px-3 py-1.5 text-white hover:bg-red-700"
            @click="handleCancel"
          >
            <Icon name="times-circle" class="size-4" />
          </button>
          <button
            v-else
            class="rounded-md bg-purple-600 px-3 py-1.5 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="
              isSending ||
              isStopping ||
              isDraftSubmitting ||
              (!newMessage.trim() && pendingAttachments.length === 0) ||
              isTokenLimitExceeded
            "
            @click="handleSend"
          >
            <Icon name="arrow-circle-right" class="size-4" />
          </button>
        </div>
      </div>

      <!-- Attach image button -->
      <p class="mt-2 text-xxs text-gray-500">
        {{
          streamState.isStreaming
            ? "Click the button to cancel"
            : "Enter to send, Shift+Enter for new line"
        }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from "vue";
import Icon from "@/components/Core/Icon.vue";
import FyrAiLogo from "@/components/Core/FyrAiLogo.vue";
import {
  useAISessionQuery,
  useAISessionMessagesQuery,
  type AIAgentMessage,
  type ChatAttachment,
} from "@/services/ai_agent";
import ReasoningEffortSelect from "@/components/AIAgent/ReasoningEffortSelect.vue";
import {
  returnUnsentDraft,
  useAiChatSession,
} from "@/composables/useAiChatSession";
import {
  useAIAgentStream,
  getApiErrorMessage,
  messagePosted,
  postingMessage,
  TurnCancelledError,
  type StreamResult,
} from "@/composables/useAIAgentStream";
import { useAIContext } from "@/composables/useAIContext";
import {
  formatCredits,
  useIsAITokenLimitExceeded,
} from "@/services/ai_token_usage";
import { DateTime } from "luxon";
import { renderMarkdown, parseAIContent } from "@/composables/useMarkdown";
import {
  usePendingAttachments,
  IMAGE_ONLY_FALLBACK_MESSAGE,
} from "@/composables/usePendingAttachments";
import { downloadFile } from "@/utils/download";
import AIContextBox from "@/components/AIAgent/AIContextBox.vue";
import AIToolCallSummary from "@/components/AIAgent/AIToolCallSummary.vue";
import DropZoneOverlay from "@/components/AIAgent/DropZoneOverlay.vue";
import PendingAttachmentList from "@/components/AIAgent/PendingAttachmentList.vue";
import { useTextareaAutosize } from "@vueuse/core";
import { useQueryClient } from "@tanstack/vue-query";
import { isAxiosError } from "axios";

const props = defineProps({
  sessionId: {
    type: Number,
    required: true,
  },
  initialMessage: {
    type: String as unknown as () => string | null,
    default: null,
  },
  initialAttachments: {
    type: Array as () => ChatAttachment[] | undefined,
    default: undefined,
  },
});

const emit = defineEmits({
  "update:initialMessage": (value: string | null) =>
    typeof value === "string" || value === null,
  "update:initialAttachments": (_value: ChatAttachment[] | undefined) => true,
});

const {
  chatEffort,
  draft: newMessage,
  draftAttachments,
  isDraftSubmitting,
  startDraftSubmit,
} = useAiChatSession();

const {
  pendingAttachments,
  attachmentWarning,
  isDragging,
  handleFileSelect,
  handlePaste,
  onDragOver,
  onDragLeave,
  onDrop,
  removePendingAttachment,
  uploadPendingAttachments,
  releasePendingAttachments,
} = usePendingAttachments(draftAttachments);

const queryClient = useQueryClient();
const sessionIdRef = computed(() => props.sessionId);
const session = useAISessionQuery(sessionIdRef);
const messages = useAISessionMessagesQuery(sessionIdRef);
const {
  state: streamState,
  sendMessageStreaming,
  resumeStreaming,
  refreshUnfollowedTurn,
  cancelSession,
} = useAIAgentStream(sessionIdRef);
const { context } = useAIContext();
const { isExceeded: isTokenLimitExceeded, usage } = useIsAITokenLimitExceeded();

// Served rather than derived from the model name: the effort vocabulary
// differs within the GPT-5 family, which a client-side list of
// effort-capable models cannot express. Older cached payloads have no such
// field, so an absent value means "no control" rather than "all levels".
const supportedEfforts = computed(
  () => session.data?.supportedReasoningEfforts ?? [],
);

// ANSWER_CLASS and renderAnswer serve the finished and the streaming answer
// alike, so a table can't scroll in one and overflow in the other. A wide
// table scrolls inside its wrapper (renderMarkdown's wrapTables). Being a
// scroll container, the wrapper stops the table's margins collapsing into the
// paragraphs' around it, so it takes them over.
const ANSWER_CLASS =
  "prose prose-sm max-w-none wrap-break-word text-gray-900 [&_a]:break-all [&_.md-table-scroll]:my-6 [&_.md-table-scroll]:overflow-x-auto [&_.md-table-scroll:first-child]:mt-0 [&_.md-table-scroll:last-child]:mb-0 [&_.md-table-scroll>table]:my-0 [&_.md-table-scroll]:focus-visible:ring-2 [&_.md-table-scroll]:focus-visible:ring-purple-600 [&_.md-table-scroll]:focus-visible:outline-none";

// Each answer's table regions are named "Answer N, table M: …", because the
// screen reader's list of regions spans the whole chat and each render
// numbers its tables from 1. N counts every assistant message the server
// holds, including the tool-call steps displayMessages hides mid-stream, so
// an answer keeps its number when the stream finishes; later messages only
// ever append.
const answerNumbers = computed(() => {
  const numbers = new Map<number, number>();
  for (const message of messages.data ?? []) {
    if (message.role === "assistant") numbers.set(message.id, numbers.size + 1);
  }
  return numbers;
});

// While it streams, an answer's tables stay out of the tab order: v-html
// replaces them on every chunk, so focus in one would be lost. They become
// reachable once the answer is finished.
function renderAnswer(
  content: string,
  { streaming = false, answer }: { streaming?: boolean; answer?: number } = {},
): string {
  return renderMarkdown(content, {
    wrapTables: true,
    focusableTables: !streaming,
    tableLabelPrefix: answer === undefined ? undefined : `Answer ${answer}`,
  });
}

const THINKING_MESSAGES = [
  "Analyzing your data...",
  "Digging into the numbers...",
  "Looking at the details...",
  "Crunching the metrics...",
  "Connecting the dots...",
  "Finding insights...",
  "Almost there...",
  "Putting it all together...",
  "One more look...",
  "Wrapping up the analysis...",
];

const thinkingMessage = computed(() => {
  const iteration = streamState.value.iteration;
  const idx = Math.min(iteration, THINKING_MESSAGES.length - 1);
  return THINKING_MESSAGES[idx];
});

const completedToolCalls = computed(() =>
  streamState.value.toolCalls.filter((tc) => tc.status === "complete"),
);

const runningToolCall = computed(() =>
  streamState.value.toolCalls.find((tc) => tc.status === "running"),
);

const TOOL_RUNNING_LABELS: Record<string, string> = {
  list_entities: "Looking up entities...",
  get_metrics: "Analyzing performance...",
  get_search_terms: "Analyzing search terms...",
  get_website_metrics: "Analyzing website traffic...",
  get_search_console: "Fetching search console data...",
  get_social_page_metrics: "Analyzing social media...",
  get_social_posts: "Analyzing social posts...",
  get_ecommerce_metrics: "Fetching e-commerce data...",
  get_store_metrics: "Fetching store data...",
  get_audience: "Analyzing audience...",
  get_heatmap: "Computing heatmap...",
  get_share_of_search: "Computing share of search...",
  get_competitor_followers: "Fetching competitor followers...",
  get_competitor_metrics_comparison: "Comparing against competitors...",
  get_reference: "Reading reference docs...",
  get_available_metrics: "Checking available metrics...",
  get_conversion_metrics: "Analyzing conversions...",
  create_dashboard: "Creating dashboard...",
  update_dashboard: "Updating dashboard...",
  create_widget_simple: "Adding widget...",
  create_goal_or_kpi_widget: "Adding goal/KPI widget...",
  manage_tab: "Updating tabs...",
  manage_widgets: "Updating widgets...",
  manage_competitor: "Managing competitor...",
  manage_goal: "Managing goal...",
  manage_kpi: "Managing KPI...",
  manage_annotation: "Managing annotation...",
};

const runningToolLabel = computed(() => {
  if (!runningToolCall.value) return "";
  return (
    TOOL_RUNNING_LABELS[runningToolCall.value.name] ??
    runningToolCall.value.name.replace(/_/g, " ") + "..."
  );
});

const messagesContainer = ref<HTMLElement | null>(null);
const { textarea: messageInput } = useTextareaAutosize({ input: newMessage });
const fileInput = ref<HTMLInputElement | null>(null);
const isSending = ref(false);
const isStopping = ref(false);
let unmounted = false;
let turnCheck: Promise<void> | null = null;
let turnKnown = false;
const TURN_CHECK_RETRY_MS = 3000;
const TURN_CHECK_ATTEMPTS = 20;
const RESTORE_READ_ATTEMPTS = 20;
const TURN_CHECK_RETRYING = "Couldn't load this chat. Retrying…";
const pendingUserMessage = ref<string | null>(null);
const pendingUserAttachments = ref<ChatAttachment[]>([]);

// Filter out system messages from display, and append pending user message
const displayMessages = computed<AIAgentMessage[]>(() => {
  let serverMessages = messages.data
    ? messages.data.filter((m) => m.role !== "system")
    : [];

  // While streaming, hide intermediate assistant messages (those with
  // toolCalls) from the current turn — the streaming indicator already
  // shows tool-call progress.  Without this, a mid-stream cache refresh
  // (e.g. refetchOnWindowFocus on tab switch) pulls intermediate
  // messages and shows tool calls in both the message list and the
  // streaming indicator.
  if (streamState.value.isStreaming && serverMessages.length > 0) {
    let lastUserIdx = -1;
    for (let i = serverMessages.length - 1; i >= 0; i--) {
      if (serverMessages[i].role === "user") {
        lastUserIdx = i;
        break;
      }
    }
    if (lastUserIdx >= 0) {
      serverMessages = serverMessages.filter(
        (m, i) =>
          i <= lastUserIdx ||
          !(m.role === "assistant" && m.toolCalls && m.toolCalls.length > 0),
      );
    }
  }

  if (pendingUserMessage.value) {
    // Check whether the server already persisted this user message
    // (refetch can complete before finally{} clears pendingUserMessage).
    // Only compare the last user message to avoid false positives when
    // the user intentionally sends the same text twice.
    const lastUserMsg = serverMessages.findLast((m) => m.role === "user");
    function attUrls(atts?: ChatAttachment[]) {
      return (atts ?? [])
        .map((a) => a.url)
        .sort()
        .join("|");
    }
    const alreadySaved =
      lastUserMsg?.content === pendingUserMessage.value &&
      attUrls(lastUserMsg?.attachments) ===
        attUrls(pendingUserAttachments.value);
    if (!alreadySaved) {
      return [
        ...serverMessages,
        {
          id: -1,
          role: "user",
          content: pendingUserMessage.value,
          attachments:
            pendingUserAttachments.value.length > 0
              ? pendingUserAttachments.value
              : undefined,
          tokensUsed: 0,
          createdAt: new Date().toISOString(),
        },
      ];
    }
  }
  return serverMessages;
});

// Format timestamp
function formatTime(timestamp: string): string {
  return DateTime.fromISO(timestamp).toFormat("h:mm a");
}

// Scroll the messages container to the bottom.  Does a synchronous
// scroll first (forces the browser to reflow immediately so new
// content is measured), then a deferred scroll via RAF to catch any
// content added in later microtasks / Vue patches.
function scrollToBottom() {
  const el = messagesContainer.value;
  if (!el) return;
  el.scrollTop = el.scrollHeight;
  requestAnimationFrame(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
}

// Auto-scroll to bottom when new messages arrive
watch(
  () => displayMessages.value.length,
  async () => {
    await nextTick();
    scrollToBottom();
  },
  { immediate: true },
);

// Scroll to bottom and focus input on mount
watch(
  () => props.sessionId,
  async () => {
    await nextTick();
    await nextTick();
    scrollToBottom();
    messageInput.value?.focus();
  },
  { immediate: true },
);

// Auto-scroll when streaming content updates
watch(
  () => streamState.value.currentMessage,
  async () => {
    await nextTick();
    scrollToBottom();
  },
);

// Auto-scroll when streaming starts or stops, and focus input when done
watch(
  () => streamState.value.isStreaming,
  async (isStreaming) => {
    await nextTick();
    scrollToBottom();
    if (!isStreaming) {
      messageInput.value?.focus();
    }
  },
);

// Auto-scroll when tool calls update
watch(
  () => streamState.value.toolCalls.length,
  async () => {
    await nextTick();
    scrollToBottom();
  },
);

// Auto-send initial message passed from parent via prop
watch(
  () => props.initialMessage,
  async (message) => {
    if (!message || !props.sessionId || streamState.value.isStreaming) return;
    const sentTo = props.sessionId;
    const lastIdBefore = messages.data?.at(-1)?.id ?? null;
    const attachments = props.initialAttachments;
    pendingUserMessage.value = message;
    pendingUserAttachments.value = attachments ?? [];
    const beforeId = lastAssistantId();
    // Cleared up front so a remount mid-turn doesn't resend it.
    emit("update:initialMessage", null);
    emit("update:initialAttachments", undefined);
    try {
      const result = await sendMessageStreaming(
        message,
        context.value,
        attachments,
        chatEffort.value,
      );
      await syncAfterTurn(result, beforeId);
    } catch (error) {
      streamState.value.error = getApiErrorMessage(
        error,
        "Failed to send message",
      );
      restoreAfterFailedSend(error, sentTo, lastIdBefore, message, attachments);
    } finally {
      pendingUserMessage.value = null;
      pendingUserAttachments.value = [];
    }
  },
  { immediate: true },
);

// In the background, so neither a hung cancel nor a run of failed reads holds
// the send box. After a Stop, only once the cancel is accepted: a failed
// cancel leaves the run, and its message, in place.
function restoreAfterFailedSend(
  error: unknown,
  sentTo: number,
  lastIdBefore: number | null,
  message: string,
  attachments: ChatAttachment[] | undefined,
): void {
  function restore(): Promise<void> {
    return restoreUnsavedMessage(sentTo, lastIdBefore, message, attachments);
  }
  if (!(error instanceof TurnCancelledError)) {
    void restore();
    return;
  }
  void error.cancelPosted.then((cancelled) =>
    cancelled ? restore() : undefined,
  );
}

// A newer message with the same text and images reads as this one saved.
async function restoreUnsavedMessage(
  sentTo: number,
  lastIdBefore: number | null,
  message: string,
  attachments: ChatAttachment[] | undefined,
): Promise<void> {
  // Read `fresh.data`, not `messages.data`: an unmounted panel's query stops
  // updating. Only a fresh read can show the message was never saved, so a
  // failed one (offline, say) is retried, for a while.
  let fresh = await messages.refetch();
  for (let attempt = 1; fresh.isError; attempt++) {
    const status = isAxiosError(fresh.error)
      ? fresh.error.response?.status
      : undefined;
    if (status === 403 || status === 404) return;
    // A duplicate resend costs less than a lost message.
    if (attempt >= RESTORE_READ_ATTEMPTS) break;
    await new Promise((resolve) => setTimeout(resolve, TURN_CHECK_RETRY_MS));
    fresh = await messages.refetch();
  }
  function imageKeys(images: ChatAttachment[] | undefined): string {
    return (images ?? [])
      .map((att) => att.key)
      .sort()
      .join("\n");
  }
  const saved =
    !fresh.isError &&
    (fresh.data ?? []).some(
      (m) =>
        m.role === "user" &&
        m.content.trim() === message.trim() &&
        imageKeys(m.attachments) === imageKeys(attachments) &&
        (lastIdBefore === null || m.id > lastIdBefore),
    );
  if (saved) return;
  const images = attachments ?? [];
  returnUnsentDraft(
    sentTo,
    images.length > 0 && message === IMAGE_ONLY_FALLBACK_MESSAGE ? "" : message,
    // Already uploaded: the placeholder file only carries the name and type a
    // resend reads.
    images.map((att) => ({
      file: new File([], att.filename, { type: att.contentType }),
      previewUrl: att.url,
      uploading: false,
      uploaded: { url: att.url, key: att.key },
    })),
  );
}

function checkTurn(id: number): Promise<void> {
  const check = checkForUnfinishedTurn(id).finally(() => {
    if (turnCheck === check) turnCheck = null;
  });
  turnCheck = check;
  return check;
}

watch(
  () => props.sessionId,
  (id) => {
    if (props.initialMessage) {
      turnKnown = true;
      return;
    }
    return checkTurn(id);
  },
  { immediate: true },
);

// A run from before turns has no turn id; an unanswered user message is the
// only sign of it.
function awaitsAnswer(history: AIAgentMessage[]): boolean {
  for (let i = history.length - 1; i >= 0; i--) {
    const message = history[i];
    if (message.role === "user") return true;
    if (message.role === "assistant" && !message.toolCalls?.length) {
      return false;
    }
  }
  return false;
}

async function checkForUnfinishedTurn(id: number): Promise<void> {
  // A read that went out before the post landed shows the turn before it.
  const cancelRefetch = postingMessage(id);
  await messagePosted(id);
  // `cancelRefetch` alone joins a pending read of a query with no data yet.
  if (cancelRefetch) {
    await Promise.all([
      queryClient.cancelQueries({ queryKey: ["ai_agent", "messages", id] }),
      queryClient.cancelQueries({ queryKey: ["ai_agent", "session", id] }),
    ]);
  }
  // Not on cached data, which can predate the turn.
  for (let attempt = 1; ; attempt++) {
    const [fresh, current] = await Promise.all([
      messages.refetch({ cancelRefetch }),
      session.refetch({ cancelRefetch }),
    ]);
    if (unmounted || id !== props.sessionId) return;
    if (!fresh.isError && !current.isError) break;
    const error = fresh.error ?? current.error;
    // A refused or missing session won't load on a retry.
    const status = isAxiosError(error) ? error.response?.status : undefined;
    if (status === 403 || status === 404) {
      streamState.value.error = getApiErrorMessage(
        error,
        "Failed to load this chat",
      );
      return;
    }
    if (attempt >= TURN_CHECK_ATTEMPTS) {
      streamState.value.error = "This chat couldn't be loaded.";
      return;
    }
    streamState.value.error = TURN_CHECK_RETRYING;
    await new Promise((resolve) => setTimeout(resolve, TURN_CHECK_RETRY_MS));
  }
  if (streamState.value.error === TURN_CHECK_RETRYING) {
    streamState.value.error = null;
  }
  turnKnown = true;
  // Even without a lease: one read can't tell a dead run from a flushed lease.
  // "Active" alone isn't a turn: a new chat whose first message never reached
  // a run is active too.
  const running =
    session.data?.status === "active" &&
    (Boolean(session.data.turn) ||
      session.data.runAlive === true ||
      awaitsAnswer(messages.data ?? []));
  if (!running) {
    refreshUnfollowedTurn(messages.data ?? [], session.data);
  }
  if (streamState.value.isStreaming || !running) return;
  void followResumedTurn();
}

async function followResumedTurn(): Promise<void> {
  const beforeId = lastAssistantId();
  try {
    await syncAfterTurn(
      await resumeStreaming(session.data?.turn ?? null),
      beforeId,
    );
  } catch (error) {
    streamState.value.error = getApiErrorMessage(
      error,
      "Failed to load the response",
    );
  }
}

function stripMetaBlocks(content: string): string {
  return content
    .replace(/---suggestions\s*\n[\s\S]*?(\n---\s*$|$)/m, "")
    .replace(/---context\s*\n[\s\S]*?(\n---\s*$|$)/m, "")
    .replace(/\n---(?:context|suggestions)[\s\S]*$/m, "")
    .replace(/\n---\s*$/m, "")
    .trimEnd();
}

function isLastAssistantMessage(messageId: number): boolean {
  const assistantMessages = displayMessages.value.filter(
    (m) => m.role === "assistant",
  );
  return (
    assistantMessages.length > 0 &&
    assistantMessages[assistantMessages.length - 1].id === messageId
  );
}

// After streaming resolves, the final assistant message may not be in
// the DB yet (backend race: session status updates before the last
// message is committed).  Start a background poll that keeps
// refetching until the response appears — this way handleSend
// unblocks immediately and the response shows up automatically.
const BACKGROUND_POLL_INTERVAL = 2000;
const BACKGROUND_POLL_MAX_DURATION = 60_000;
let backgroundPollTimer: ReturnType<typeof setInterval> | null = null;

function lastAssistantId(): number | null {
  if (!messages.data) return null;
  for (let i = messages.data.length - 1; i >= 0; i--) {
    const m = messages.data[i];
    if (m.role === "assistant" && (!m.toolCalls || m.toolCalls.length === 0)) {
      return m.id;
    }
  }
  return null;
}

function stopBackgroundPoll() {
  if (backgroundPollTimer) {
    clearInterval(backgroundPollTimer);
    backgroundPollTimer = null;
  }
}

function startBackgroundPoll(beforeId: number | null) {
  stopBackgroundPoll();
  const start = Date.now();
  let inFlight = false;
  backgroundPollTimer = setInterval(async () => {
    if (inFlight) return;
    if (Date.now() - start > BACKGROUND_POLL_MAX_DURATION) {
      stopBackgroundPoll();
      return;
    }
    inFlight = true;
    try {
      await messages.refetch();
      const newId = lastAssistantId();
      if (newId !== null && newId !== beforeId) {
        stopBackgroundPoll();
        session.refetch();
      }
    } finally {
      inFlight = false;
    }
  }, BACKGROUND_POLL_INTERVAL);
}

async function syncAfterTurn(
  result: StreamResult,
  beforeId: number | null,
): Promise<void> {
  // A poll started after unmount is never stopped.
  if (unmounted) return;
  if (!result.messagesAlreadyCached) {
    await messages.refetch();
    if (unmounted) return;
    const newId = lastAssistantId();
    if (newId === null || newId === beforeId) {
      startBackgroundPoll(beforeId);
    }
  }
  session.refetch();
}

function handleSuggestionClick(suggestion: string) {
  if (isDraftSubmitting.value) return;
  newMessage.value = suggestion;
  handleSend();
}

async function handleSend() {
  // Captured up front: the draft is shared, so by the time the upload finishes
  // it may hold text typed for another chat.
  const submittedDraft = newMessage.value;
  const submittedAttachments = [...pendingAttachments.value];
  const hasText = submittedDraft.trim().length > 0;
  const hasAttachments = submittedAttachments.length > 0;
  if (
    (!hasText && !hasAttachments) ||
    !props.sessionId ||
    isSending.value ||
    isStopping.value ||
    isDraftSubmitting.value ||
    streamState.value.isStreaming
  ) {
    return;
  }
  // A check that gave up: an outage may have passed since.
  if (!turnKnown && !turnCheck) {
    isSending.value = true;
    try {
      await checkTurn(props.sessionId);
    } finally {
      isSending.value = false;
    }
    if (unmounted || !turnKnown) return;
  }

  isSending.value = true;
  const submission = startDraftSubmit();

  // Upload attachments first
  let attachments: ChatAttachment[] | undefined;
  if (hasAttachments) {
    let uploaded: ChatAttachment[] | null = null;
    try {
      uploaded = await uploadPendingAttachments();
    } finally {
      // Also on a throw: a submission left open locks the shared draft.
      if (!uploaded) {
        isSending.value = false;
        submission.end();
      }
    }
    if (!uploaded) return; // upload failed, don't send
    attachments = uploaded;
  }

  // Not before knowing whether a turn is running: concurrent runs would edit
  // the same dashboard.
  if (turnCheck) await turnCheck;
  // Unmounted: the panel now on screen owns the draft and may already have
  // started a turn.
  if (unmounted || !turnKnown || streamState.value.isStreaming) {
    isSending.value = false;
    submission.end();
    return;
  }

  const message = submittedDraft.trim() || IMAGE_ONLY_FALLBACK_MESSAGE;
  if (submission.isCurrent()) newMessage.value = "";
  pendingUserMessage.value = message;
  pendingUserAttachments.value = attachments ?? [];
  releasePendingAttachments(submittedAttachments);
  submission.end();

  const sentTo = props.sessionId;
  const lastIdBefore = messages.data?.at(-1)?.id ?? null;
  try {
    const beforeId = lastAssistantId();
    const result = await sendMessageStreaming(
      message,
      context.value,
      attachments,
      chatEffort.value,
    );
    await syncAfterTurn(result, beforeId);
  } catch (error) {
    streamState.value.error = getApiErrorMessage(
      error,
      "Failed to send message",
    );
    restoreAfterFailedSend(error, sentTo, lastIdBefore, message, attachments);
  } finally {
    isSending.value = false;
    pendingUserMessage.value = null;
    pendingUserAttachments.value = [];
  }
}

// Send waits at most this long for a Stop to land; one landing later still
// names its own turn.
const STOP_LANDING_MAX_MS = 10_000;

async function handleCancel() {
  if (!props.sessionId) return;

  isStopping.value = true;
  const capped = setTimeout(() => {
    isStopping.value = false;
  }, STOP_LANDING_MAX_MS);
  try {
    await cancelSession();
    messages.refetch();
    session.refetch();
  } catch (error) {
    console.error("Failed to cancel session:", error);
    // The run goes on, and the chat should go on following it.
    if (!unmounted) void checkTurn(props.sessionId);
  } finally {
    clearTimeout(capped);
    isStopping.value = false;
  }
}

// --- Code block copy/download buttons (event delegation) ---

function enhanceCodeBlocks(container: HTMLElement) {
  const codeBlocks = container.querySelectorAll("pre > code");
  for (const codeEl of codeBlocks) {
    const pre = codeEl.parentElement!;
    if (pre.dataset.enhanced) continue;
    pre.dataset.enhanced = "true";
    pre.classList.add("group", "relative");

    const lang = detectLanguage(codeEl);

    const toolbar = document.createElement("div");
    toolbar.className = "absolute top-1 right-1 hidden gap-1 group-hover:flex";
    toolbar.appendChild(createToolbarButton("Copy", "copy"));
    toolbar.appendChild(createToolbarButton("Download", "download"));

    pre.dataset.lang = lang;
    pre.appendChild(toolbar);
  }
}

function detectLanguage(codeEl: Element): string {
  for (const cls of codeEl.classList) {
    if (cls.startsWith("language-")) return cls.slice(9);
  }
  const text = (codeEl.textContent || "").trim();
  if (
    (text.startsWith("{") && text.endsWith("}")) ||
    (text.startsWith("[") && text.endsWith("]"))
  ) {
    return "json";
  }
  return "text";
}

function createToolbarButton(label: string, action: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.textContent = label;
  btn.dataset.codeAction = action;
  btn.className =
    "rounded bg-gray-700/80 px-2 py-0.5 text-[10px] text-white hover:bg-gray-600";
  return btn;
}

function handleCodeBlockAction(e: MouseEvent) {
  const btn = (e.target as HTMLElement).closest<HTMLButtonElement>(
    "[data-code-action]",
  );
  if (!btn) return;
  e.stopPropagation();

  const pre = btn.closest("pre");
  const codeEl = pre?.querySelector("code");
  if (!pre || !codeEl) return;

  const text = codeEl.textContent || "";
  const action = btn.dataset.codeAction;

  if (action === "copy") {
    navigator.clipboard.writeText(text);
    btn.textContent = "Copied!";
    setTimeout(() => {
      btn.textContent = "Copy";
    }, 1500);
  } else if (action === "download") {
    const lang = pre.dataset.lang || "text";
    const ext = lang === "json" ? ".json" : ".txt";
    const mime = lang === "json" ? "application/json" : "text/plain";
    downloadFile(text, `ai-output${ext}`, mime);
  }
}

onMounted(() => {
  messagesContainer.value?.addEventListener("click", handleCodeBlockAction);
});

onBeforeUnmount(() => {
  unmounted = true;
  messagesContainer.value?.removeEventListener("click", handleCodeBlockAction);
  stopBackgroundPoll();
});

// Enhance code blocks when messages change or streaming stops
watch(
  [() => displayMessages.value.length, () => streamState.value.isStreaming],
  async () => {
    await nextTick();
    if (!messagesContainer.value) return;
    const contentDivs =
      messagesContainer.value.querySelectorAll(".ai-chat-content");
    for (const div of contentDivs) {
      enhanceCodeBlocks(div as HTMLElement);
    }
  },
);
</script>
